import type { LucideIcon } from "lucide-react";

/**
 * A KPI tile — the AdminLTE "small box" pattern: a big number, a label, an icon, and an optional
 * link to where the number came from. Built on the same semantic colour tokens as everything else
 * (tailwind.config.ts), so it inherits the Seplat palette and dark mode for free rather than
 * hardcoding a hex value per tone.
 */
const TONES = {
  primary: "bg-primary/10 text-primary",
  accent: "bg-accent/10 text-accent",
  muted: "bg-muted text-muted-foreground",
  destructive: "bg-destructive/10 text-destructive",
} as const;

export const StatCard = ({
  label,
  value,
  icon: Icon,
  tone = "primary",
  onClick,
}: {
  label: string;
  value: string | number;
  icon: LucideIcon;
  tone?: keyof typeof TONES;
  onClick?: () => void;
}): JSX.Element => {
  const Wrapper = onClick ? "button" : "div";

  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`flex items-center gap-4 rounded-lg border bg-card p-4 text-left shadow-sm transition-colors ${
        onClick ? "hover:border-primary/40 hover:bg-muted/30" : ""
      }`}
    >
      <span
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md ${TONES[tone]}`}
      >
        <Icon aria-hidden className="h-5 w-5" />
      </span>
      <span className="min-w-0">
        <span className="block font-heading text-2xl font-semibold tabular-nums leading-tight">
          {value}
        </span>
        <span className="block truncate text-xs text-muted-foreground">{label}</span>
      </span>
    </Wrapper>
  );
};
